/**
 * BpmnEventNode - Eventos BPMN (início, intermediário e fim)
 *
 * Círculo fino para início (verde), círculo duplo âmbar para evento
 * intermediário/espera e círculo grosso vermelho para fim, com o rótulo abaixo
 * do símbolo. Apresentação pura.
 *
 * @version 3.0.0
 * RELEASE: 2026-08-31 v3.0.0 - Cores semânticas por tipo de evento.
 * RELEASE: 2026-08-31 v2.0.0 - Fluxo vertical (entrada no topo, saída na base)
 *          e botão "+" para continuar o ramo.
 * RELEASE: 2026-08-31 v1.0.0 - Criação inicial (designer BPMN)
 */

import { Handle, Position, type NodeProps } from '@xyflow/react';
import { Play, Timer, Flag, Zap, ShieldAlert } from 'lucide-react';
import { ESTILO_DA_FORMA, type BpmnShape } from './bpmnShapes';
import { BpmnAppendButton } from './BpmnAppendButton';
import type { WorkflowPaletteItem } from '../workflowNodeMeta';

export interface BpmnEventNodeData extends Record<string, unknown> {
  label: string;
  shape: BpmnShape;
  resumo?: string | null;
  /** v3.1.0: fichas legíveis das regras do filtro do gatilho */
  filtros?: string[];
  podeEditar?: boolean;
  semSaida?: boolean;
  onAdicionar?: (nodeId: string, item: WorkflowPaletteItem) => void;
}

export function BpmnEventNode({ id, data, selected }: NodeProps) {
  const node = data as BpmnEventNodeData;
  const inicio = node.shape === 'evento_inicio';
  const fim = node.shape === 'evento_fim';
  const estilo = ESTILO_DA_FORMA[node.shape];

  const exigencia = node.shape === 'evento_exigencia';
  const Icon = inicio ? Play : fim ? Flag : exigencia ? ShieldAlert : Timer;

  // Espessura do círculo conforme a notação BPMN + cor semântica do tipo
  const anelClasse = inicio
    ? `border-2 ${estilo.borda} ${estilo.selo}`
    : fim
      ? `border-[4px] ${estilo.borda} ${estilo.selo}`
      : `border-2 ${estilo.borda} ${estilo.selo} ring-2 ring-warning/40 ring-offset-2 ring-offset-background`;

  return (
    <div className="flex w-[200px] flex-col items-center gap-1.5">
      {!inicio && <Handle type="target" position={Position.Top} className="!h-2.5 !w-2.5" />}

      <div
        className={`flex h-14 w-14 items-center justify-center rounded-full transition-shadow ${anelClasse} ${
          selected ? 'shadow-lg ring-2 ring-primary ring-offset-2 ring-offset-background' : ''
        }`}
      >
        <Icon className="h-5 w-5" />
      </div>

      <p className="max-w-[200px] truncate text-center text-xs font-medium text-foreground">{node.label}</p>
      {node.resumo && (
        <p className="flex max-w-[200px] items-center gap-1 truncate text-center text-[11px] text-muted-foreground">
          {inicio && <Zap className="h-3 w-3 shrink-0 text-success" />}
          {node.resumo}
        </p>
      )}

      {/* v3.1.0: regras do filtro do gatilho visíveis no próprio bloco */}
      {(node.filtros?.length ?? 0) > 0 && (
        <div className="flex w-[200px] flex-col gap-0.5 rounded-lg border bg-card/80 px-1.5 py-1">
          {node.filtros!.map((linha, i) => (
            <span key={i} className="truncate text-[10px] leading-tight text-muted-foreground" title={linha}>
              {linha}
            </span>
          ))}
        </div>
      )}


      {!fim && node.podeEditar && node.semSaida && (
        <BpmnAppendButton onSelect={(item) => node.onAdicionar?.(id, item)} />
      )}

      {!fim && <Handle type="source" position={Position.Bottom} className="!h-2.5 !w-2.5" />}
    </div>
  );
}
